import React, { useContext, useMemo } from "react";
import { ConfigProvider, Tabs, theme } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import { getCookie } from "../../helpers/localStorage";
import { Role } from "../../constants/defaultKeys";
import useIsMobile from "../../hooks/useIsMobile";
import styles from "./orgSettings.module.css";

const { useToken } = theme;

const OrgTabs = () => {
  const navigate = useNavigate();
  const router = useLocation();
  const { userDetails } = useContext(UserContext);
  const isMobile = useIsMobile();
  const { token } = useToken();
  const activeKey = router?.pathname?.split("/")?.[2];

  const onChange = (key) => {
    navigate(`/organization-settings/${key}`, { replace: true });
  };

  const defaultRole = userDetails?.currRole || getCookie(Role);
  const canSeeSettings =
    defaultRole === "Organization_Owner" ||
    defaultRole === "Organization_Admin";

  const items = useMemo(() => {
    const base = [
      {
        key: "org-details",
        label: isMobile ? "Details" : "Basic details",
      },
      {
        key: "contact",
        label: isMobile ? "Contact" : "Contact and address",
      },
      {
        key: "additional-information",
        label: isMobile ? "Additional" : "Additional information",
      },
    ];
    if (canSeeSettings) {
      base.push({ key: "settings", label: "Settings" });
    }
    return base;
  }, [canSeeSettings, isMobile]);

  const resolvedActiveKey =
    items.find((item) => item.key === activeKey)?.key || items[0]?.key;

  if (isMobile) {
    return (
      <div className={styles.orgSegmented} role="tablist">
        {items.map((item) => {
          const isActive = resolvedActiveKey === item.key;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`${styles.orgSegment} ${
                isActive ? styles.orgSegmentActive : ""
              }`}
              style={isActive ? { color: token.colorPrimary } : undefined}
              onClick={() => onChange(item.key)}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className={styles.orgTabs}>
      <ConfigProvider
        theme={{
          components: {
            Tabs: {
              horizontalItemPadding: "0",
              horizontalItemGutter: 24,
              colorBorderSecondary: "transparent",
              titleFontSize: 14,
            },
          },
        }}
      >
        <Tabs
          activeKey={resolvedActiveKey}
          onChange={onChange}
          tabBarStyle={{ margin: 0 }}
          items={items.map((item) => ({
            key: item.key,
            label: (
              <span
                style={{
                  color:
                    resolvedActiveKey === item.key
                      ? token.colorPrimary
                      : token.colorText,
                  whiteSpace: "nowrap",
                }}
              >
                {item.label}
              </span>
            ),
          }))}
        />
      </ConfigProvider>
    </div>
  );
};

export default OrgTabs;
